import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { inr } from "@/components/bits";
import api, { errMsg } from "@/lib/api";

export function AiJobMatches() {
  const [state, setState] = useState({ loading: true, matches: [], needsProfile: false, error: false });
  const [refreshing, setRefreshing] = useState(false);

  const fetchMatches = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    try {
      const { data } = await api.get(`/ai/job-matches${refresh ? "?refresh=true" : ""}`);
      setState({ loading: false, matches: data.matches || [], needsProfile: !!data.needs_profile, error: false });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: true }));
      if (refresh) toast.error(errMsg(e));
    } finally {
      setRefreshing(false);
    }
  };
  useEffect(() => {
    fetchMatches();
  }, []);

  return (
    <div className="bg-white border border-border/60 rounded-xl p-6" data-testid="ai-matches-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-semibold text-[#0B1B3D] flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#F5A623]" /> Recommended for you
        </h3>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => fetchMatches(true)} disabled={refreshing} data-testid="ai-matches-refresh-btn">
          <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      {state.loading ? (
        <div className="space-y-3" data-testid="ai-matches-loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="border border-border/50 rounded-lg p-4 animate-pulse">
              <div className="h-4 bg-slate-100 rounded w-2/3" />
              <div className="h-3 bg-slate-100 rounded w-1/3 mt-2" />
            </div>
          ))}
        </div>
      ) : state.error ? (
        <div className="text-center py-4" data-testid="ai-matches-error">
          <p className="text-sm text-muted-foreground">Could not load recommendations right now.</p>
          <Button size="sm" variant="outline" className="rounded-full mt-3" onClick={() => fetchMatches(true)} data-testid="ai-matches-retry-btn">
            Try again
          </Button>
        </div>
      ) : state.needsProfile ? (
        <p className="text-sm text-muted-foreground" data-testid="ai-matches-needs-profile">
          Complete your profile (headline, skills, location) to unlock AI-powered job recommendations.
        </p>
      ) : state.matches.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="ai-matches-empty">
          No new matches right now — check back as new jobs are published.
        </p>
      ) : (
        <div className="space-y-3" data-testid="ai-matches-list">
          {state.matches.map((m) => (
            <Link key={m.job.id} to={`/jobs/${m.job.id}`} className="block" data-testid={`ai-match-${m.job.id}`}>
              <div className="border border-border/60 rounded-lg p-4 card-hover h-full">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-semibold text-sm text-[#0B1B3D]">{m.job.title}</p>
                  <span className="text-[11px] font-bold text-[#2563EB] bg-[#2563EB]/8 rounded-full px-2.5 py-0.5 shrink-0" data-testid={`ai-match-score-${m.job.id}`}>
                    {m.score}% match
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{m.job.company_name} · {m.job.location}</p>
                <p className="text-xs font-semibold mt-2 text-[#2563EB]">{inr(m.job.salary_min)} – {inr(m.job.salary_max)}</p>
                {m.explanation && (
                  <p className="text-[11px] text-muted-foreground mt-2 leading-snug border-t border-border/40 pt-2">{m.explanation}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
